import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http, apiError } from "@/lib/api";
import { ArrowLeft, FloppyDisk, Plus, Trash, X, PencilSimple, Copy, CheckSquare, CalendarBlank } from "@phosphor-icons/react";

const blankExercise = () => ({ name: "", sets: 3, reps: "8-12", weight_target: "", notes: "" });
const blankDay = (i = 1) => ({ day_name: `Day ${i}`, focus: "", exercises: [blankExercise()] });
const MAX_WEEKS = 12;

/**
 * Deep-clones a list of workout days so week-to-week copies don't share
 * exercise/set object references (React state mutations would leak across weeks).
 */
const cloneDays = (days) =>
  (days || []).map((d) => ({
    day_name: d.day_name || "",
    focus: d.focus || "",
    exercises: (d.exercises || []).map((ex) => ({ ...ex })),
  }));

/**
 * Reads a plan doc off the server and returns the weeks_plan we'll edit locally.
 * Rules:
 *   - If plan has non-empty weeks_plan → use it as-is (source of truth).
 *   - Else fall back to plan.days repeated N times so every week is editable.
 *   - If plan is brand new / empty → seed one week with a single blank day.
 */
const buildWeeksPlanState = (plan) => {
  const n = Math.max(1, Math.min(MAX_WEEKS, plan?.weeks || 4));
  const existing = Array.isArray(plan?.weeks_plan) ? plan.weeks_plan : null;
  const baseDays = plan?.days?.length ? plan.days : [blankDay(1)];
  if (existing && existing.length > 0) {
    const byWeek = new Map(existing.map((w) => [w.week, w.days || []]));
    return Array.from({ length: n }, (_, i) => ({
      week: i + 1,
      days: cloneDays(byWeek.get(i + 1) || baseDays),
    }));
  }
  return Array.from({ length: n }, (_, i) => ({ week: i + 1, days: cloneDays(baseDays) }));
};

export default function CoachClientPlan() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [plan, setPlan] = useState(null); // null = none, object = active
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  // form state
  const [name, setName] = useState("Custom Plan");
  const [description, setDescription] = useState("");
  const [weeks, setWeeks] = useState(4);
  // weeks_plan: [{week, days: [...]}] — one entry per week. Editor always renders per-week.
  const [weeksPlan, setWeeksPlan] = useState(() => buildWeeksPlanState(null));
  const [activeWeek, setActiveWeek] = useState(1); // 1-indexed
  const [coachNotes, setCoachNotes] = useState("");
  const [dailyHabits, setDailyHabits] = useState([
    { id: "", label: "" }, { id: "", label: "" }, { id: "", label: "" }, { id: "", label: "" }, { id: "", label: "" },
  ]);
  // copy-from
  const [otherClients, setOtherClients] = useState([]);
  const [showCopyPicker, setShowCopyPicker] = useState(false);
  const [copyingFrom, setCopyingFrom] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const [d, p, cs] = await Promise.all([
        http.get(`/coach/clients/${id}`),
        http.get(`/coach/clients/${id}/plan`),
        http.get(`/coach/clients`),
      ]);
      setClient(d.data.client);
      const planData = p.data;
      setPlan(planData);
      if (planData) {
        setName(planData.name || "Custom Plan");
        setDescription(planData.description || "");
        setWeeks(planData.weeks || 4);
        setWeeksPlan(buildWeeksPlanState(planData));
        setActiveWeek(1);
        setCoachNotes(planData.coach_notes || "");
        const h = (planData.daily_habits || []).slice(0, 6);
        while (h.length < 6) h.push({ id: "", label: "" });
        setDailyHabits(h);
      }
      setOtherClients((cs.data || []).filter((c) => c.id !== id && c.active_program?.source === "plan"));
    } catch (e) { setErr(apiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  // Whenever `weeks` shrinks/grows, resize `weeksPlan`. Growing adds new weeks
  // pre-populated by cloning the last defined week so the coach has a starting
  // point (progressive overload usually iterates on the previous week).
  useEffect(() => {
    const n = Math.max(1, Math.min(MAX_WEEKS, Number(weeks) || 1));
    setWeeksPlan((cur) => {
      if (cur.length === n) return cur;
      if (cur.length > n) return cur.slice(0, n);
      const seed = cur[cur.length - 1]?.days || [blankDay(1)];
      const added = Array.from({ length: n - cur.length }, (_, i) => ({
        week: cur.length + i + 1,
        days: cloneDays(seed),
      }));
      return [...cur, ...added];
    });
    setActiveWeek((w) => Math.min(w, n));
  }, [weeks]);

  const activeDays = weeksPlan.find((w) => w.week === activeWeek)?.days || [];

  // -------- editing helpers (all operate on the currently-active week) --------
  const patchActiveWeekDays = (updater) => {
    setWeeksPlan((cur) => cur.map((w) => (w.week === activeWeek ? { ...w, days: updater(w.days) } : w)));
  };
  const updateDay = (i, patch) => patchActiveWeekDays((days) => days.map((d, idx) => idx === i ? { ...d, ...patch } : d));
  const updateExercise = (di, ei, patch) => patchActiveWeekDays((days) =>
    days.map((d, idx) => idx !== di ? d : { ...d, exercises: d.exercises.map((ex, j) => j !== ei ? ex : { ...ex, ...patch }) })
  );
  const addDay = () => patchActiveWeekDays((days) => [...days, blankDay(days.length + 1)]);
  const removeDay = (i) => patchActiveWeekDays((days) => days.filter((_, idx) => idx !== i));
  const addExercise = (di) => patchActiveWeekDays((days) =>
    days.map((d, idx) => idx !== di ? d : { ...d, exercises: [...d.exercises, blankExercise()] })
  );
  const removeExercise = (di, ei) => patchActiveWeekDays((days) =>
    days.map((d, idx) => idx !== di ? d : { ...d, exercises: d.exercises.filter((_, j) => j !== ei) })
  );
  const moveExercise = (di, ei, dir) => patchActiveWeekDays((days) =>
    days.map((d, idx) => {
      if (idx !== di) return d;
      const arr = [...d.exercises];
      const j = ei + dir;
      if (j < 0 || j >= arr.length) return d;
      [arr[ei], arr[j]] = [arr[j], arr[ei]];
      return { ...d, exercises: arr };
    })
  );

  // Copy an entire week's days into the active week (progressive-overload bootstrap).
  const copyFromWeek = (sourceWeek) => {
    if (sourceWeek === activeWeek) return;
    const source = weeksPlan.find((w) => w.week === sourceWeek);
    if (!source) return;
    if (!window.confirm(`Overwrite Week ${activeWeek} with a copy of Week ${sourceWeek}?`)) return;
    setWeeksPlan((cur) => cur.map((w) => (w.week === activeWeek ? { ...w, days: cloneDays(source.days) } : w)));
    setMsg(`Copied Week ${sourceWeek} into Week ${activeWeek}. Tweak and save.`);
    setTimeout(() => setMsg(""), 2500);
  };

  // -------- save --------
  const save = async () => {
    setSaving(true); setErr(""); setMsg("");
    try {
      // Build sanitised weeks_plan. Week 1 doubles as the legacy `days` payload
      // so any code path that still reads `plan.days` (older clients / mobile
      // shells cached before the multi-week roll-out) still gets Week 1 content.
      const sanitizedWeeksPlan = weeksPlan.map((w) => ({
        week: w.week,
        days: (w.days || []).map((d) => ({
          day_name: d.day_name || "Day",
          focus: d.focus || "",
          exercises: (d.exercises || [])
            .filter((e) => (e.name || "").trim())
            .map((e) => ({
              name: e.name.trim(),
              sets: Number(e.sets) || 3,
              reps: e.reps || "",
              weight_target: e.weight_target || "",
              notes: e.notes || "",
            })),
        })),
      }));
      const week1Days = sanitizedWeeksPlan[0]?.days || [];
      const body = {
        name: name.trim() || "Custom Plan",
        description,
        weeks: Number(weeks) || 4,
        days: week1Days,
        weeks_plan: sanitizedWeeksPlan,
        coach_notes: coachNotes,
        daily_habits: dailyHabits
          .filter((h) => (h.label || "").trim())
          .map((h) => ({ id: h.id || undefined, label: h.label.trim() })),
      };
      const url = `/coach/clients/${id}/plan`;
      const { data } = plan ? await http.put(url, body) : await http.post(url, body);
      setPlan(data);
      setWeeksPlan(buildWeeksPlanState(data));
      setActiveWeek((w) => Math.min(w, data.weeks || 1));
      // refresh habit ids assigned by server
      const h = (data.daily_habits || []).slice(0, 6);
      while (h.length < 6) h.push({ id: "", label: "" });
      setDailyHabits(h);
      setMsg("Plan saved.");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) { setErr(apiError(e)); }
    finally { setSaving(false); }
  };

  const duplicateFrom = async () => {
    if (!copyingFrom) return;
    if (!window.confirm("Replace this client's current plan with a copy of the selected client's plan? Current plan (if any) will be archived.")) return;
    setSaving(true); setErr("");
    try {
      const { data } = await http.post(`/coach/clients/${id}/plan/duplicate-from/${copyingFrom}`);
      setPlan(data);
      setName(data.name || "Custom Plan");
      setDescription(data.description || "");
      setWeeks(data.weeks || 4);
      setWeeksPlan(buildWeeksPlanState(data));
      setActiveWeek(1);
      setCoachNotes(data.coach_notes || "");
      const h = (data.daily_habits || []).slice(0, 6);
      while (h.length < 6) h.push({ id: "", label: "" });
      setDailyHabits(h);
      setCopyingFrom("");
      setShowCopyPicker(false);
      setMsg("Plan duplicated. Tweak anything you like.");
      setTimeout(() => setMsg(""), 3500);
    } catch (e) { setErr(apiError(e)); }
    finally { setSaving(false); }
  };

  const archive = async () => {
    if (!window.confirm("Archive this client's active plan? You can build a new one anytime.")) return;
    try {
      await http.delete(`/coach/clients/${id}/plan`);
      setPlan(null);
      setName("Custom Plan"); setDescription(""); setWeeks(4);
      setWeeksPlan(buildWeeksPlanState(null));
      setActiveWeek(1);
      setCoachNotes("");
    } catch (e) { setErr(apiError(e)); }
  };

  const totalExercises = useMemo(
    () => activeDays.reduce((acc, d) => acc + d.exercises.filter((e) => (e.name || "").trim()).length, 0),
    [activeDays]
  );

  if (loading) return <div className="p-10 id-label id-pulse">Loading plan...</div>;

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto">
      <Link to={`/coach/clients/${id}`} className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-xs uppercase tracking-[0.2em] mb-6" data-testid="back-to-client">
        <ArrowLeft size={14} weight="bold" /> Back to {client?.name || "Client"}
      </Link>

      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="id-label mb-3">— Plan Editor —</div>
          <h1 className="id-h1">{plan ? "Edit Plan" : "Build New Plan"}</h1>
          <p className="text-zinc-500 font-manrope text-sm mt-1">
            {client?.name} · {plan ? "Editing this client's active plan" : "No active plan — building from scratch"}
          </p>
          {client?.training_location && (
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="plan-training-setup">
              <span className="px-2.5 py-1 border border-[#FF3333] bg-[#FF3333]/10 text-[10px] font-bold tracking-[0.2em] uppercase text-[#FF3333]">
                Trains at: {client.training_location}
              </span>
              {(client.training_location === "home" || client.training_location === "both") && (
                <>
                  {(client.home_equipment || []).map((eq) => (
                    <span key={eq} className="px-2.5 py-1 border border-[#262626] bg-[#0F0F0F] text-[11px] font-manrope text-zinc-300">{eq}</span>
                  ))}
                  {client.home_equipment_other && (
                    <span className="px-2.5 py-1 border border-[#262626] bg-[#0F0F0F] text-[11px] font-manrope text-zinc-300">+ {client.home_equipment_other}</span>
                  )}
                  {(!client.home_equipment || client.home_equipment.length === 0) && !client.home_equipment_other && (
                    <span className="px-2.5 py-1 border border-zinc-700 text-[11px] font-manrope text-zinc-500">No equipment specified</span>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {!plan && otherClients.length > 0 && (
            <button onClick={() => setShowCopyPicker((s) => !s)} className="id-btn-secondary" data-testid="copy-from-button">
              <Copy size={14} weight="bold" /> Copy from another client
            </button>
          )}
          {plan && (
            <button onClick={archive} className="id-btn-secondary" data-testid="archive-plan-button">
              <Trash size={14} weight="bold" /> Archive
            </button>
          )}
          <button onClick={save} disabled={saving} className="id-btn-primary" data-testid="save-plan-button">
            <FloppyDisk size={16} weight="bold" /> {saving ? "Saving..." : plan ? "Save Changes" : "Create Plan"}
          </button>
        </div>
      </div>

      {showCopyPicker && (
        <div className="id-card mb-6 border-[#FF3333]" data-testid="copy-picker">
          <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
            <div className="id-label flex-shrink-0">Copy from</div>
            <select
              value={copyingFrom}
              onChange={(e) => setCopyingFrom(e.target.value)}
              className="id-input flex-1"
              data-testid="copy-source-select"
            >
              <option value="">— Select a client with an active plan —</option>
              {otherClients.map((c) => (
                <option key={c.id} value={c.id}>{c.name} · {c.active_program?.name}</option>
              ))}
            </select>
            <button onClick={duplicateFrom} disabled={!copyingFrom || saving} className="id-btn-primary" data-testid="copy-confirm-button">
              <Copy size={14} weight="bold" /> Copy
            </button>
            <button onClick={() => { setShowCopyPicker(false); setCopyingFrom(""); }} className="id-btn-ghost" data-testid="copy-cancel-button">
              Cancel
            </button>
          </div>
        </div>
      )}

      {err && <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333] mb-4" data-testid="plan-error">{err}</div>}
      {msg && <div className="border border-[#00FF66]/40 bg-[#00FF66]/10 px-4 py-3 text-sm text-[#00FF66] mb-4" data-testid="plan-success">{msg}</div>}

      {/* Plan meta */}
      <div className="id-card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="id-label block mb-2">Plan Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="id-input" data-testid="plan-name-input" placeholder="e.g. John — Cut Phase 1" />
          </div>
          <div>
            <label className="id-label block mb-2">Weeks</label>
            <input type="number" min={1} max={MAX_WEEKS} value={weeks} onChange={(e) => setWeeks(e.target.value)} className="id-input" data-testid="plan-weeks-input" />
          </div>
        </div>
        <div className="mt-4">
          <label className="id-label block mb-2">Plan Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="id-input" placeholder="Goals, intent, anything the client should know..." data-testid="plan-description-input" />
        </div>
      </div>

      {/* Daily Habits — The 7 Lazy Lean Habits */}
      <div className="id-card mb-6">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <CheckSquare size={16} weight="bold" className="text-[#FF3333]" />
            <div className="id-label">Daily Habits (up to 7)</div>
          </div>
          <button
            type="button"
            onClick={() => setDailyHabits([
              { id: "", label: "🥩 Protein First" },
              { id: "", label: "🚶 10,000 Steps Daily" },
              { id: "", label: "💧 Drink Your Water" },
              { id: "", label: "🥦 Build Better Plates" },
              { id: "", label: "🌙 Kitchen Closed After Dinner" },
              { id: "", label: "🪞 Identity Check" },
              { id: "", label: "😴 Sleep — 6 Hours Minimum", key: "sleep_6h" },
            ])}
            className="text-xs uppercase tracking-[0.15em] font-bold border border-[#FF3333] text-[#FF3333] px-3 py-1.5 hover:bg-[#FF3333] hover:text-white transition-colors"
            data-testid="apply-lazy-lean-defaults-btn"
            title="Replace the 7 habits below with the canonical Lazy Lean Habits"
          >
            Apply 7 Lazy Lean Habits™
          </button>
        </div>
        <p className="text-zinc-500 text-xs font-manrope mb-3">
          The 7 Lazy Lean Habits™ the client ticks off every day. Weekly score (out of 49) and 14-day streak show on their dashboard. Click the button above to seed the canonical set, or edit each row to customise.
        </p>
        <div className="space-y-2" data-testid="daily-habits-list">
          {dailyHabits.map((h, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="id-label w-6 text-zinc-500">{i + 1}</span>
              <input
                value={h.label}
                onChange={(e) => setDailyHabits((cur) => cur.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))}
                placeholder={[
                  "🥩 Protein First",
                  "🚶 10,000 Steps Daily",
                  "💧 Drink Your Water",
                  "🥦 Build Better Plates",
                  "🌙 Kitchen Closed After Dinner",
                  "🪞 Identity Check",
                  "😴 Sleep — 6 Hours Minimum",
                ][i] || "Add a habit"}
                className="id-input flex-1"
                data-testid={`daily-habit-${i}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Habits & Nutrition Notes */}
      <div className="id-card mb-6">
        <div className="flex items-center gap-2 mb-3">
          <PencilSimple size={16} weight="bold" className="text-[#FF3333]" />
          <div className="id-label">Habits & Nutrition Notes</div>
        </div>
        <p className="text-zinc-500 text-xs font-manrope mb-3">
          Personalised meal/habit guidance the client sees on every dashboard. Markdown-friendly text.
        </p>
        <textarea
          value={coachNotes}
          onChange={(e) => setCoachNotes(e.target.value)}
          rows={8}
          className="id-input font-mono text-sm"
          placeholder={"Daily Habits:\n1. 10,000 steps\n2. 30g protein at breakfast\n3. No food after 8pm\n4. 2L water\n5. Lights out before 11pm\n\nMeal guidance:\n- Lean protein at every meal\n- 2 fists of veg minimum at lunch & dinner..."}
          data-testid="plan-coach-notes-input"
        />
      </div>

      {/* Workouts — week tabs + editor */}
      <div className="flex items-center gap-2 mb-3">
        <CalendarBlank size={16} weight="bold" className="text-[#FF3333]" />
        <div className="id-label">Workouts · Week-by-Week</div>
      </div>
      <p className="text-zinc-500 text-xs font-manrope mb-3">
        Each week has its own days & exercises so you can build progressive overload (e.g. Week 1 volume → Week 2 intensity → Week 3 deload). Click a week tab to edit that week. New weeks are seeded from the previous week — tweak from there.
      </p>

      {/* Week tab bar */}
      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="week-tabs">
        {weeksPlan.map((w) => {
          const isActive = w.week === activeWeek;
          const dayCount = w.days.length;
          const exCount = w.days.reduce((acc, d) => acc + (d.exercises || []).filter((e) => (e.name || "").trim()).length, 0);
          return (
            <button
              key={w.week}
              onClick={() => setActiveWeek(w.week)}
              className={
                "px-3 py-2 text-xs font-manrope uppercase tracking-[0.15em] border transition-colors " +
                (isActive
                  ? "border-[#FF3333] bg-[#FF3333]/10 text-white"
                  : "border-[#262626] text-zinc-500 hover:text-white hover:border-zinc-500")
              }
              data-testid={`week-tab-${w.week}`}
              title={`${dayCount} days · ${exCount} exercises`}
            >
              Week {w.week}
            </button>
          );
        })}
      </div>

      {/* Copy-from-week toolbar (shown only when >1 week exists) */}
      {weeksPlan.length > 1 && (
        <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="copy-week-toolbar">
          <span className="id-label text-zinc-500">Bootstrap Week {activeWeek} from:</span>
          {weeksPlan
            .filter((w) => w.week !== activeWeek)
            .map((w) => (
              <button
                key={w.week}
                onClick={() => copyFromWeek(w.week)}
                className="text-[10px] font-manrope uppercase tracking-[0.15em] border border-[#262626] hover:border-[#FF3333] hover:text-[#FF3333] text-zinc-400 px-2 py-1"
                data-testid={`copy-from-week-${w.week}`}
                title={`Overwrite Week ${activeWeek} with Week ${w.week}'s days`}
              >
                Copy Week {w.week}
              </button>
            ))}
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <div className="id-label">Week {activeWeek} · Workouts</div>
        <div className="text-zinc-500 text-xs font-manrope">{activeDays.length} day{activeDays.length !== 1 ? "s" : ""} · {totalExercises} exercise{totalExercises !== 1 ? "s" : ""}</div>
      </div>

      <div className="space-y-3" data-testid="plan-days-list">
        {activeDays.map((day, di) => (
          <div key={di} className="id-card" data-testid={`plan-day-${di}`}>
            <div className="flex items-center gap-3 mb-3">
              <input
                value={day.day_name}
                onChange={(e) => updateDay(di, { day_name: e.target.value })}
                className="id-input max-w-[200px]"
                placeholder="Day name"
                data-testid={`plan-day-name-${di}`}
              />
              <input
                value={day.focus}
                onChange={(e) => updateDay(di, { focus: e.target.value })}
                className="id-input flex-1"
                placeholder="Focus (Push, Pull, Full Body, Rest...)"
                data-testid={`plan-day-focus-${di}`}
              />
              {activeDays.length > 1 && (
                <button onClick={() => removeDay(di)} className="text-zinc-500 hover:text-[#FF3333] p-2" data-testid={`plan-remove-day-${di}`}>
                  <Trash size={18} weight="bold" />
                </button>
              )}
            </div>

            <div className="space-y-2">
              {day.exercises.map((ex, ei) => (
                <div key={ei} className="border border-[#262626] p-3 bg-[#0F0F0F]" data-testid={`plan-ex-${di}-${ei}`}>
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <input value={ex.name} onChange={(e) => updateExercise(di, ei, { name: e.target.value })} className="id-input col-span-12 md:col-span-4" placeholder="Exercise name" data-testid={`plan-ex-name-${di}-${ei}`} />
                    <input type="number" min={1} value={ex.sets} onChange={(e) => updateExercise(di, ei, { sets: e.target.value })} className="id-input col-span-3 md:col-span-1" placeholder="Sets" data-testid={`plan-ex-sets-${di}-${ei}`} />
                    <input value={ex.reps} onChange={(e) => updateExercise(di, ei, { reps: e.target.value })} className="id-input col-span-4 md:col-span-2" placeholder="Reps" data-testid={`plan-ex-reps-${di}-${ei}`} />
                    <input value={ex.weight_target} onChange={(e) => updateExercise(di, ei, { weight_target: e.target.value })} className="id-input col-span-4 md:col-span-2" placeholder="Target weight" data-testid={`plan-ex-weight-${di}-${ei}`} />
                    <div className="col-span-1 flex gap-1 justify-end">
                      <button onClick={() => moveExercise(di, ei, -1)} disabled={ei === 0} className="text-zinc-500 hover:text-white disabled:opacity-30 text-xs px-1" data-testid={`plan-ex-up-${di}-${ei}`}>↑</button>
                      <button onClick={() => moveExercise(di, ei, 1)} disabled={ei === day.exercises.length - 1} className="text-zinc-500 hover:text-white disabled:opacity-30 text-xs px-1" data-testid={`plan-ex-down-${di}-${ei}`}>↓</button>
                    </div>
                    <button onClick={() => removeExercise(di, ei)} className="col-span-1 text-zinc-500 hover:text-[#FF3333] justify-self-end" data-testid={`plan-ex-remove-${di}-${ei}`}>
                      <X size={18} weight="bold" />
                    </button>
                  </div>
                  <input
                    value={ex.notes || ""}
                    onChange={(e) => updateExercise(di, ei, { notes: e.target.value })}
                    className="id-input mt-2 text-sm"
                    placeholder="Coach note for this exercise (tempo, cue, swap rule...)"
                    data-testid={`plan-ex-notes-${di}-${ei}`}
                  />
                </div>
              ))}
              <button onClick={() => addExercise(di)} className="id-btn-ghost text-xs" data-testid={`plan-add-ex-${di}`}>
                <Plus size={14} weight="bold" /> Add exercise
              </button>
            </div>
          </div>
        ))}
      </div>

      <button onClick={addDay} className="id-btn-secondary w-full mt-4" data-testid="plan-add-day-button">
        <Plus size={14} weight="bold" /> Add Workout Day to Week {activeWeek}
      </button>

      <div className="mt-8 flex items-center justify-end gap-3">
        <button onClick={save} disabled={saving} className="id-btn-primary" data-testid="save-plan-button-bottom">
          <FloppyDisk size={16} weight="bold" /> {saving ? "Saving..." : plan ? "Save Changes" : "Create Plan"}
        </button>
      </div>
    </div>
  );
}
